import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PublicUser, UserRole } from '@messe/shared'
import { passwordSchema } from '@messe/shared'
import { Button } from './ui/button.js'
import { SelectField, TextField } from './ui/field.js'
import { Alert } from './ui/feedback.js'
import { Dialog } from './ui/dialog.js'
import { t } from '../i18n/index.js'
import { ApiError } from '../lib/api.js'
import { createUser, generatePassphrase, updateUser } from '../lib/users.js'
import { listValueListItems, listValueLists } from '../lib/value-lists.js'

interface Props {
  open: boolean
  /** Null when creating, the account when editing. */
  user: PublicUser | null
  onClose: () => void
  onSaved: (message: string) => void
}

interface FormState {
  name: string
  email: string
  password: string
  role: UserRole
  /** Empty string means no assignment. */
  buyerItemId: string
}

const EMPTY: FormState = { name: '', email: '', password: '', role: 'capturer', buyerItemId: '' }

/**
 * Creating and editing share one dialog because they differ in exactly two
 * ways: the email is fixed once assigned, and a password is only set at
 * creation.
 */
export function UserFormDialog({ open, user, onClose, onSaved }: Props) {
  const strings = t()
  const queryClient = useQueryClient()
  const isEditing = user !== null

  // The buyers list drives the assignment dropdown. Loaded only while the
  // dialog is open, because most sessions never open it.
  const lists = useQuery({ queryKey: ['value-lists'], queryFn: listValueLists, enabled: open })
  const buyersListId = lists.data?.find((list) => list.key === 'buyers')?.id

  const buyers = useQuery({
    queryKey: ['value-list-items', buyersListId, 'buyers-for-assignment'],
    queryFn: () => listValueListItems(buyersListId ?? '', { limit: 200, sort: 'label' }),
    enabled: open && buyersListId !== undefined,
  })

  const [values, setValues] = useState<FormState>(EMPTY)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return

    setValues(
      user
        ? {
            name: user.name,
            email: user.email,
            password: '',
            role: user.role,
            buyerItemId: user.buyerItemId ?? '',
          }
        : { ...EMPTY, password: generatePassphrase() },
    )
    setFieldErrors({})
    setFormError(null)
  }, [open, user])

  const save = useMutation({
    mutationFn: async () => {
      const buyerItemId = values.buyerItemId === '' ? null : values.buyerItemId

      if (user) {
        return updateUser(user.id, { name: values.name, role: values.role, buyerItemId })
      }
      return createUser({
        name: values.name,
        email: values.email,
        password: values.password,
        role: values.role,
        buyerItemId,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] })
      onSaved(isEditing ? strings.users.edit.success : strings.users.create.success)
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        setFormError(
          error.status === 409
            ? strings.users.create.duplicate
            : (error.problem.detail ?? strings.errors.unexpected),
        )
        return
      }
      setFormError(strings.errors.unexpected)
    },
  })

  function validate(): boolean {
    const errors: Record<string, string> = {}

    if (values.name.trim() === '') errors['name'] = strings.errors.required
    if (!isEditing) {
      if (values.email.trim() === '') errors['email'] = strings.errors.required
      const password = passwordSchema.safeParse(values.password)
      if (!password.success) {
        errors['password'] = password.error.issues[0]?.message ?? strings.errors.required
      }
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEditing ? strings.users.edit.heading : strings.users.create.heading}
      description={isEditing ? undefined : strings.users.create.description}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {strings.users.actions.cancel}
          </Button>
          <Button
            disabled={save.isPending}
            onClick={() => {
              setFormError(null)
              if (validate()) save.mutate()
            }}
          >
            {save.isPending
              ? strings.users.actions.saving
              : isEditing
                ? strings.users.actions.save
                : strings.users.create.submit}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {formError && <Alert tone="danger">{formError}</Alert>}

        <TextField
          label={strings.users.create.name}
          value={values.name}
          error={fieldErrors['name']}
          onChange={(event) => setValues((v) => ({ ...v, name: event.target.value }))}
        />

        <TextField
          label={strings.users.create.email}
          type="email"
          autoComplete="off"
          value={values.email}
          disabled={isEditing}
          {...(isEditing ? { hint: strings.users.edit.emailImmutable } : {})}
          error={fieldErrors['email']}
          onChange={(event) => setValues((v) => ({ ...v, email: event.target.value }))}
        />

        {!isEditing && (
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <TextField
                label={strings.users.create.password}
                hint={strings.users.create.passwordHint}
                autoComplete="new-password"
                value={values.password}
                error={fieldErrors['password']}
                onChange={(event) => setValues((v) => ({ ...v, password: event.target.value }))}
              />
            </div>
            <Button
              variant="secondary"
              className="mb-6"
              onClick={() => setValues((v) => ({ ...v, password: generatePassphrase() }))}
            >
              {strings.users.create.generate}
            </Button>
          </div>
        )}

        <SelectField
          label={strings.users.create.buyer}
          hint={strings.users.create.buyerHint}
          value={values.buyerItemId}
          options={[
            { value: '', label: strings.users.create.buyerNone },
            ...(buyers.data?.items ?? []).map((entry) => ({
              value: entry.id,
              label: entry.label,
            })),
          ]}
          onChange={(event) => setValues((v) => ({ ...v, buyerItemId: event.target.value }))}
        />

        <SelectField
          label={strings.users.create.role}
          value={values.role}
          options={[
            { value: 'capturer', label: strings.users.roles.capturer },
            { value: 'admin', label: strings.users.roles.admin },
          ]}
          onChange={(event) => setValues((v) => ({ ...v, role: event.target.value as UserRole }))}
        />
      </div>
    </Dialog>
  )
}
